import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, User as UserIcon } from "lucide-react";

// Italian fiscal billing form. Used for both User SaaS billing and Client invoicing.
// scope="user" hides the first/last_name fields for "private" (the SaaS user already has profile name).
// For now we keep the same fields for both scopes to keep the form predictable.
export function BillingForm({ value, onChange, scope = "user" }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const type = v.type || "";
  const isPrivate = type === "private";
  const isBusiness = type === "business";

  return (
    <div className="space-y-5" data-testid={`billing-form-${scope}`}>
      {/* Type selector */}
      <div>
        <Label className="label-uppercase">Tipo fatturazione *</Label>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => set("type", "private")}
            className={`flex items-center gap-3 p-4 border rounded-sm text-left transition-colors ${
              isPrivate ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
            }`}
            data-testid="billing-type-private"
          >
            <UserIcon className={`w-5 h-5 ${isPrivate ? "text-primary" : "text-neutral-500"}`} strokeWidth={1.5} />
            <div>
              <div className="font-medium text-sm">Privato</div>
              <div className="text-xs text-neutral-500">Persona fisica · Codice fiscale</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => set("type", "business")}
            className={`flex items-center gap-3 p-4 border rounded-sm text-left transition-colors ${
              isBusiness ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
            }`}
            data-testid="billing-type-business"
          >
            <Building2 className={`w-5 h-5 ${isBusiness ? "text-primary" : "text-neutral-500"}`} strokeWidth={1.5} />
            <div>
              <div className="font-medium text-sm">Azienda</div>
              <div className="text-xs text-neutral-500">P.IVA · SDI o PEC</div>
            </div>
          </button>
        </div>
      </div>

      {!type && (
        <p className="text-xs text-neutral-500 italic">
          Seleziona il tipo per visualizzare i campi richiesti.
        </p>
      )}

      {/* Private-only fields */}
      {isPrivate && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-uppercase">Nome *</Label>
            <Input
              value={v.first_name || ""}
              onChange={(e) => set("first_name", e.target.value)}
              className="mt-1 rounded-sm"
              data-testid="billing-first-name"
            />
          </div>
          <div>
            <Label className="label-uppercase">Cognome *</Label>
            <Input
              value={v.last_name || ""}
              onChange={(e) => set("last_name", e.target.value)}
              className="mt-1 rounded-sm"
              data-testid="billing-last-name"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-uppercase">Codice fiscale *</Label>
            <Input
              value={v.tax_code || ""}
              onChange={(e) => set("tax_code", e.target.value.toUpperCase())}
              maxLength={16}
              placeholder="RSSMRA80A01H501Z"
              className="mt-1 rounded-sm font-mono uppercase"
              data-testid="billing-tax-code"
            />
          </div>
        </div>
      )}

      {/* Business-only fields */}
      {isBusiness && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="label-uppercase">Ragione sociale *</Label>
            <Input
              value={v.business_name || ""}
              onChange={(e) => set("business_name", e.target.value)}
              className="mt-1 rounded-sm"
              data-testid="billing-business-name"
            />
          </div>
          <div>
            <Label className="label-uppercase">Partita IVA *</Label>
            <Input
              value={v.vat_number || ""}
              onChange={(e) => set("vat_number", e.target.value.toUpperCase().replace(/\s+/g, ""))}
              placeholder="IT12345678901"
              className="mt-1 rounded-sm font-mono uppercase"
              data-testid="billing-vat-number"
            />
          </div>
          <div>
            <Label className="label-uppercase">Codice fiscale (se diverso)</Label>
            <Input
              value={v.tax_code || ""}
              onChange={(e) => set("tax_code", e.target.value.toUpperCase())}
              maxLength={16}
              className="mt-1 rounded-sm font-mono uppercase"
              data-testid="billing-tax-code-business"
            />
          </div>
        </div>
      )}

      {/* Address — required for both */}
      {type && (
        <div className="border-t border-border pt-5">
          <div className="label-uppercase mb-3">Indirizzo di fatturazione</div>
          <div className="grid sm:grid-cols-6 gap-4">
            <div className="sm:col-span-6">
              <Label className="label-uppercase">Indirizzo *</Label>
              <Input
                value={v.address || ""}
                onChange={(e) => set("address", e.target.value)}
                placeholder="Via, numero civico"
                className="mt-1 rounded-sm"
                data-testid="billing-address"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase">Città *</Label>
              <Input
                value={v.city || ""}
                onChange={(e) => set("city", e.target.value)}
                className="mt-1 rounded-sm"
                data-testid="billing-city"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase">CAP *</Label>
              <Input
                value={v.zip || ""}
                onChange={(e) => set("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                maxLength={5}
                className="mt-1 rounded-sm"
                data-testid="billing-zip"
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="label-uppercase">Provincia *</Label>
              <Input
                value={v.province || ""}
                onChange={(e) => set("province", e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2))}
                maxLength={2}
                placeholder="LE"
                className="mt-1 rounded-sm font-mono uppercase"
                data-testid="billing-province"
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="label-uppercase">Paese</Label>
              <Input
                value={v.country || "IT"}
                onChange={(e) => set("country", e.target.value.toUpperCase().slice(0, 2))}
                maxLength={2}
                className="mt-1 rounded-sm font-mono uppercase"
                data-testid="billing-country"
              />
            </div>
          </div>
        </div>
      )}

      {/* Electronic invoicing — only for business */}
      {isBusiness && (
        <div className="border-t border-border pt-5">
          <div className="label-uppercase mb-3">Fatturazione elettronica</div>
          <p className="text-xs text-neutral-500 mb-3">
            Per le aziende è obbligatorio almeno uno tra Codice SDI o PEC.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-uppercase">Codice SDI *</Label>
              <Input
                value={v.sdi_code || ""}
                onChange={(e) => set("sdi_code", e.target.value.toUpperCase().slice(0, 7))}
                maxLength={7}
                placeholder="ABCDE12"
                className="mt-1 rounded-sm font-mono uppercase"
                data-testid="billing-sdi-code"
              />
            </div>
            <div>
              <Label className="label-uppercase">PEC *</Label>
              <Input
                type="email"
                value={v.pec_email || ""}
                onChange={(e) => set("pec_email", e.target.value)}
                placeholder="azienda@pec.it"
                className="mt-1 rounded-sm"
                data-testid="billing-pec-email"
              />
            </div>
          </div>
          <p className="text-[11px] text-neutral-500 mt-2 italic">
            * Compilare almeno uno dei due campi.
          </p>
        </div>
      )}
    </div>
  );
}

// Client-side validation that mirrors backend `_validate_billing`. Returns null if valid, or an error string.
export function validateBilling(b) {
  if (!b) return "Dati di fatturazione mancanti";
  const t = (b.type || "").toLowerCase();
  if (t !== "private" && t !== "business") return "Seleziona il tipo di fatturazione";
  if (!(b.address || "").trim()) return "Indirizzo richiesto";
  if (!(b.city || "").trim()) return "Città richiesta";
  if (!(b.zip || "").trim()) return "CAP richiesto";
  // Provincia required for IT (Italian SdI invoicing rules) — mirrors the
  // server-side gate so checkout cannot be reached with empty province.
  const country = (b.country || "IT").toUpperCase();
  if (country === "IT" && !(b.province || "").trim())
    return "Provincia richiesta (sigla 2 lettere, es. LE)";
  if (t === "private") {
    if (!(b.first_name || "").trim()) return "Nome richiesto";
    if (!(b.last_name || "").trim()) return "Cognome richiesto";
    if (!(b.tax_code || "").trim()) return "Codice fiscale richiesto";
  } else {
    if (!(b.business_name || "").trim()) return "Ragione sociale richiesta";
    if (!(b.vat_number || "").trim()) return "Partita IVA richiesta";
    if (!(b.sdi_code || "").trim() && !(b.pec_email || "").trim())
      return "Indica almeno il Codice SDI oppure la PEC";
  }
  return null;
}
